import React, { useEffect, useMemo, useState } from "react";
import { Store, Search, Plus, Pencil, Trash2, Loader2, ArrowLeft, Leaf, Clock, Save, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { PageHeader, EmptyState, LoadingBlock } from "../../components/app/ui";
import ImageField from "../../components/app/ImageField";
import { adminApi, inr2, errorMessage } from "../../lib/api";
import BulkMenuImport from "../../components/app/BulkMenuImport";

const DAYS = [
  { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" }, { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" }, { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" }, { key: "sun", label: "Sun" },
];
const DEFAULT_DAY = { open: "09:00", close: "22:00", closed: false };
const defaultHours = (h) => { const out = {}; DAYS.forEach((d) => { out[d.key] = { ...DEFAULT_DAY, ...(h?.[d.key] || {}) }; }); return out; };
const EMPTY_ITEM = { name: "", description: "", price: "", category: "", image: "", is_available: true, out_of_stock: false, veg: true };

function ItemDialog({ open, onOpenChange, item, rid, categories, onSaved }) {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(item ? { ...EMPTY_ITEM, ...item } : EMPTY_ITEM); }, [item, open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.category?.trim()) return toast.error("Please enter a category");
    setBusy(true);
    try {
      const body = { name: form.name, description: form.description || null, price: Number(form.price), category: form.category.trim(), image: form.image || null, is_available: !!form.is_available, out_of_stock: !!form.out_of_stock, veg: !!form.veg };
      const saved = item ? await adminApi.patchRestaurantItem(rid, item.item_id, body) : await adminApi.addRestaurantItem(rid, body);
      if (!item) { try { await adminApi.addRestaurantCategory(rid, form.category.trim()); } catch { /* exists */ } }
      toast.success(item ? "Item updated" : "Item added");
      onSaved(saved);
      onOpenChange(false);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="admin-menu-item-dialog">
        <DialogHeader><DialogTitle>{item ? "Edit item" : "Add menu item"}</DialogTitle><DialogDescription>Editing on behalf of the restaurant. Customers see this on the restaurant page.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2"><Label>Name</Label><Input required value={form.name} onChange={set("name")} data-testid="admin-item-name" /></div>
            <div className="space-y-2"><Label>Price (INR)</Label><Input required type="number" min="1" step="1" value={form.price} onChange={set("price")} data-testid="admin-item-price" /></div>
            <div className="space-y-2"><Label>Category</Label><Input required value={form.category} onChange={set("category")} list="admin-cat-list" placeholder="e.g. Mains" data-testid="admin-item-category" /><datalist id="admin-cat-list">{categories.map((c) => <option key={c} value={c} />)}</datalist></div>
            <div className="space-y-2 sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={set("description")} data-testid="admin-item-description" /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Photo</Label><ImageField value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testId="admin-item-image" /></div>
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-ink"><Switch checked={!!form.veg} onCheckedChange={(v) => setForm((f) => ({ ...f, veg: v }))} data-testid="admin-item-veg" /> Vegetarian</label>
            <label className="flex items-center gap-2 text-sm text-ink"><Switch checked={!!form.is_available} onCheckedChange={(v) => setForm((f) => ({ ...f, is_available: v }))} data-testid="admin-item-available" /> Available</label>
            <label className="flex items-center gap-2 text-sm text-ink"><Switch checked={!!form.out_of_stock} onCheckedChange={(v) => setForm((f) => ({ ...f, out_of_stock: v }))} data-testid="admin-item-oos" /> Out of stock</label>
          </div>
          <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="admin-item-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {item ? "Save changes" : "Add item"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProfilePanel({ restaurant, onSaved }) {
  const [name, setName] = useState(restaurant.name || "");
  const [legalName, setLegalName] = useState(restaurant.legal_name || "");
  const [hours, setHours] = useState(defaultHours(restaurant.hours));
  const [busy, setBusy] = useState(false);
  const setDay = (day, k, v) => setHours((h) => ({ ...h, [day]: { ...h[day], [k]: v } }));
  const save = async () => {
    setBusy(true);
    try { const r = await adminApi.updateRestaurant(restaurant.restaurant_id, { name, legal_name: legalName, hours }); toast.success("Restaurant updated"); onSaved(r); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="kt-card space-y-4 p-5" data-testid="admin-restaurant-profile">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-muted">Profile & hours</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Display name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-rest-name" /></div>
        <div className="space-y-2"><Label>Business (legal) name</Label><Input value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Registered name on invoices" data-testid="admin-rest-legal-name" /></div>
      </div>
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Opening hours</Label>
        <div className="space-y-2">
          {DAYS.map((d) => (
            <div key={d.key} className="flex items-center gap-3" data-testid={`admin-hours-${d.key}`}>
              <span className="w-10 text-xs font-semibold text-ink-soft">{d.label}</span>
              <label className="flex items-center gap-1.5 text-xs text-ink-soft"><Switch checked={!hours[d.key].closed} onCheckedChange={(v) => setDay(d.key, "closed", !v)} data-testid={`admin-hours-open-${d.key}`} />{hours[d.key].closed ? "Closed" : "Open"}</label>
              {!hours[d.key].closed && (
                <div className="flex items-center gap-2">
                  <Input type="time" value={hours[d.key].open} onChange={(e) => setDay(d.key, "open", e.target.value)} className="w-28" data-testid={`admin-hours-from-${d.key}`} />
                  <span className="text-ink-muted">–</span>
                  <Input type="time" value={hours[d.key].close} onChange={(e) => setDay(d.key, "close", e.target.value)} className="w-28" data-testid={`admin-hours-to-${d.key}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="admin-rest-save"><Save className="mr-2 h-4 w-4" /> {busy ? "Saving…" : "Save profile & hours"}</Button>
    </div>
  );
}

function RestaurantPanel({ rid, onBack }) {
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const loadMenu = () => adminApi.restaurantMenu(rid).then(setMenu).catch(() => setMenu([]));
  useEffect(() => { adminApi.restaurant(rid).then(setRestaurant).catch((e) => { toast.error(errorMessage(e)); onBack(); }); loadMenu(); /* eslint-disable-next-line */ }, [rid]);

  const categories = useMemo(() => {
    const seen = new Set(); const out = [];
    [...(restaurant?.categories || []), ...(menu || []).map((m) => m.category).filter(Boolean)].forEach((c) => { const k = c.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push(c); } });
    return out;
  }, [restaurant?.categories, menu]);
  const grouped = useMemo(() => {
    const g = (menu || []).reduce((acc, m) => { const k = m.category || "Uncategorised"; (acc[k] = acc[k] || []).push(m); return acc; }, {});
    return g;
  }, [menu]);

  const remove = async (m) => { if (!window.confirm(`Delete "${m.name}"?`)) return; try { await adminApi.deleteRestaurantItem(rid, m.item_id); setMenu((p) => p.filter((x) => x.item_id !== m.item_id)); toast.success("Item deleted"); } catch (e) { toast.error(errorMessage(e)); } };

  if (!restaurant) return <LoadingBlock rows={4} />;
  return (
    <div className="space-y-6">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-soft hover:text-brand" data-testid="admin-rest-back"><ArrowLeft className="h-4 w-4" /> All restaurants</button>
      <div className="flex items-center justify-between gap-3">
        <div><h2 className="text-xl font-bold text-ink" data-testid="admin-rest-title">{restaurant.name}</h2><p className="text-xs text-ink-soft">{restaurant.address}</p></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-full" data-testid="admin-import-menu"><FileSpreadsheet className="mr-2 h-4 w-4" /> Import CSV</Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="admin-add-item"><Plus className="mr-2 h-4 w-4" /> Add item</Button>
        </div>
      </div>
      <ProfilePanel restaurant={restaurant} onSaved={setRestaurant} />
      {menu === null && <LoadingBlock rows={3} />}
      {menu?.length === 0 && <EmptyState icon={Store} title="No menu items yet" text="Add the first dish on behalf of this restaurant." />}
      <div className="space-y-6">
        {Object.entries(grouped).map(([cat, items]) => (
          <section key={cat}>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink-muted" data-testid={`admin-menu-heading-${cat.toLowerCase().replace(/\s+/g, "-")}`}>{cat} <span className="rounded-full bg-surface-200 px-2 py-0.5 text-[10px] text-ink-soft">{items.length}</span></h3>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {items.map((m) => (
                <div key={m.item_id} className={`kt-card flex gap-3 p-3 ${!m.is_available ? "opacity-70" : ""}`} data-testid={`admin-menu-row-${m.item_id}`}>
                  {m.image ? <img src={m.image} alt={m.name} className="h-16 w-16 shrink-0 rounded-xl object-cover" /> : <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-surface-200 text-[10px] text-ink-muted">No photo</div>}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2"><p className="truncate text-sm font-semibold text-ink">{m.veg && <Leaf className="mr-1 inline h-3.5 w-3.5 text-graphite" />}{m.name}{m.out_of_stock && <span className="ml-1.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700">OOS</span>}</p><p className="text-sm font-bold text-ink">{inr2(m.price)}</p></div>
                    {m.description && <p className="mt-0.5 line-clamp-2 text-xs text-ink-soft">{m.description}</p>}
                    <div className="mt-auto flex justify-end gap-1 pt-2">
                      <button onClick={() => { setEditing(m); setOpen(true); }} className="rounded-lg p-1.5 text-ink-muted hover:bg-surface-100 hover:text-ink" data-testid={`admin-edit-${m.item_id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(m)} className="rounded-lg p-1.5 text-ink-muted hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-${m.item_id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      <ItemDialog open={open} onOpenChange={setOpen} item={editing} rid={rid} categories={categories} onSaved={() => loadMenu()} />
      <BulkMenuImport open={importOpen} onOpenChange={setImportOpen} onImport={(items) => adminApi.bulkAddRestaurantItems(rid, items)} onDone={loadMenu} />
    </div>
  );
}

export default function Restaurants() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { adminApi.restaurants().then(setList).catch((e) => { toast.error(errorMessage(e)); setList([]); }); }, []);
  const filtered = useMemo(() => {
    if (!list) return [];
    const ql = q.trim().toLowerCase();
    return !ql ? list : list.filter((r) => `${r.name} ${r.legal_name || ""} ${r.address || ""}`.toLowerCase().includes(ql));
  }, [list, q]);

  if (selected) return (<><PageHeader title="Manage restaurant" description="Edit menu, name and hours on behalf of the restaurant." /><RestaurantPanel rid={selected} onBack={() => setSelected(null)} /></>);

  return (
    <>
      <PageHeader title="Restaurants" description="Manage any restaurant's menu, name and opening hours on their behalf." />
      <div className="mb-5 max-w-md">
        <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search restaurants…" className="pl-9" data-testid="admin-rest-search" /></div>
      </div>
      {list === null && <LoadingBlock rows={4} />}
      {list?.length === 0 && <EmptyState icon={Store} title="No restaurants yet" text="Restaurants appear here once they sign up." />}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((r) => (
          <button key={r.restaurant_id} onClick={() => setSelected(r.restaurant_id)} className="kt-card flex items-center gap-3 p-4 text-left transition hover:ring-2 hover:ring-brand-200" data-testid={`admin-rest-card-${r.restaurant_id}`}>
            {r.image ? <img src={r.image} alt={r.name} className="h-14 w-14 shrink-0 rounded-xl object-cover" /> : <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-surface-200"><Store className="h-6 w-6 text-ink-muted" /></div>}
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{r.name}</p>
              <p className="truncate text-xs text-ink-soft">{(r.cuisines || []).slice(0, 2).join(", ") || r.address}</p>
              <p className="mt-0.5 text-xs font-medium text-brand">{r.menu_count} menu items · {r.is_open ? "Open" : "Closed"}</p>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
